/**
 * Restrições e Multas do veículo (consulta DETRAN via provedor). Mostra o status cadastral, os
 * gravames (restrição financeira), as multas, os débitos e os licenciamentos — tudo que a consulta
 * veicular gravou. O botão dispara a consulta sob demanda (além do lote mensal do worker). Sem token
 * de provedor configurado, o backend responde 503 e avisamos.
 */
import { AlertTriangle, RefreshCw, ShieldAlert } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Chip, Spinner } from '@/components/ui/misc';
import { api } from '@/lib/api';

type Multa = {
  auto_infracao: string;
  orgao: string | null;
  descricao: string | null;
  gravidade: string | null;
  pontos: number | null;
  data_infracao: string | null;
  local: string | null;
  municipio: string | null;
  valor: number | null;
  situacao: string | null;
  vencimento: string | null;
};
type Gravame = {
  tipo: string | null;
  agente: string | null;
  contrato: string | null;
  restricao: string | null;
  situacao: string | null;
};
type Debito = {
  tipo: string | null;
  exercicio: number | null;
  descricao: string | null;
  valor: number | null;
  situacao: string | null;
};
type Licenciamento = { exercicio: number; situacao: string | null; valor: number | null };
type SecaoDetalhe = {
  titulo: string;
  alerta?: boolean;
  itens: { rotulo: string; valor: string | null }[];
};
type ConsultaVeicular = {
  detalhes: SecaoDetalhe[];
  consulta_veicular_em: string | null;
  situacao: string | null;
  municipio: string | null;
  uf_placa: string | null;
  especie: string | null;
  categoria: string | null;
  procedencia: string | null;
  restricao: string | null;
  multas: Multa[];
  gravames: Gravame[];
  licenciamentos: Licenciamento[];
  debitos: Debito[];
};

function reais(v: number | null): string {
  return v === null ? '—' : v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}
function data(v: string | null): string {
  if (!v) return '';
  const [a, m, d] = v.split('-');
  return d ? `${d}/${m}/${a}` : v;
}

type NivelConsulta = { id: string; rotulo: string; descricao: string; custo: number | null };

export function PainelConsultaVeicular({ item }: { item: { id: string } }) {
  const [dados, setDados] = useState<ConsultaVeicular | null>(null);
  const [catalogo, setCatalogo] = useState<NivelConsulta[]>([]);
  const [selecao, setSelecao] = useState<Set<string>>(new Set());
  const [seletorAberto, setSeletorAberto] = useState(false);
  const [carregando, setCarregando] = useState(true);
  const [consultando, setConsultando] = useState(false);

  async function carregar() {
    const r = await api.get<ConsultaVeicular>(`/cadastro/veiculos/${item.id}/consulta-veicular`);
    setCarregando(false);
    if (r.ok) setDados(r.dados);
  }

  useEffect(() => {
    setCarregando(true);
    void carregar();
    void api
      .get<NivelConsulta[]>('/cadastro/veiculos/consulta-veicular/catalogo')
      .then((r) => r.ok && setCatalogo(r.dados));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.id]);

  const custoTotal = catalogo
    .filter((n) => selecao.has(n.id))
    .reduce((s, n) => s + (n.custo ?? 0), 0);

  function alternar(id: string) {
    setSelecao((atual) => {
      const nova = new Set(atual);
      if (nova.has(id)) nova.delete(id);
      else nova.add(id);
      return nova;
    });
  }
  function todas() {
    setSelecao((atual) =>
      atual.size === catalogo.length ? new Set() : new Set(catalogo.map((n) => n.id)),
    );
  }

  async function consultar() {
    if (consultando || selecao.size === 0) return;
    setConsultando(true);
    const aviso = toast.loading('Consultando… pode levar alguns segundos.');
    const r = await api.post<{ sucesso: number; multas: number }>(
      `/cadastro/veiculos/${item.id}/consulta-veicular`,
      { consultas: [...selecao] },
    );
    toast.dismiss(aviso);
    setConsultando(false);
    if (!r.ok) return void toast.error(r.erro);
    if (r.dados.sucesso) toast.success('Consulta concluída.');
    else toast('A consulta não retornou dados — veja o registro no histórico.');
    setSeletorAberto(false);
    await carregar();
  }

  const temAlgo =
    dados &&
    (dados.situacao ||
      dados.restricao ||
      dados.detalhes.length ||
      dados.multas.length ||
      dados.gravames.length ||
      dados.debitos.length ||
      dados.licenciamentos.length);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <ShieldAlert className="size-4 text-accent" />
          Restrições e Multas
        </CardTitle>
        <Button
          variant="secundario"
          size="sm"
          onClick={() => setSeletorAberto((v) => !v)}
          title="Escolher quais consultas rodar"
        >
          <RefreshCw />
          Consultar…
        </Button>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {/* Seletor de níveis: o operador escolhe QUAIS consultas rodar (cada uma tem custo). */}
        {seletorAberto && (
          <div className="flex flex-col gap-2 rounded-md border border-border bg-surface-2 p-3">
            <div className="flex items-center justify-between">
              <span className="tipo-rotulo text-text-tertiary">
                Escolha as consultas (cada uma é cobrada à parte)
              </span>
              <button
                type="button"
                onClick={todas}
                className="tipo-corpo-pequeno text-accent hover:underline"
              >
                {selecao.size === catalogo.length ? 'Limpar' : 'Selecionar todas'}
              </button>
            </div>
            {catalogo.map((n) => (
              <label key={n.id} className="flex cursor-pointer items-start gap-2">
                <input
                  type="checkbox"
                  checked={selecao.has(n.id)}
                  onChange={() => alternar(n.id)}
                  className="mt-0.5 size-4 accent-[var(--primary)]"
                />
                <span className="flex flex-1 flex-col">
                  <span className="flex items-center justify-between gap-2 text-[15px] text-text">
                    {n.rotulo}
                    {n.custo !== null && (
                      <span className="tipo-corpo-pequeno text-text-tertiary">
                        ~{reais(n.custo)}
                      </span>
                    )}
                  </span>
                  {n.descricao && (
                    <span className="tipo-corpo-pequeno text-text-tertiary">{n.descricao}</span>
                  )}
                </span>
              </label>
            ))}
            <div className="mt-1 flex items-center justify-between">
              <span className="tipo-corpo-pequeno text-text-secondary">
                {selecao.size} selecionada(s) · ~{reais(custoTotal)}
              </span>
              <Button
                variant="primario"
                size="sm"
                onClick={() => void consultar()}
                disabled={consultando || selecao.size === 0}
              >
                <RefreshCw className={consultando ? 'animate-spin' : undefined} />
                Consultar
              </Button>
            </div>
          </div>
        )}

        {carregando ? (
          <Spinner />
        ) : !temAlgo ? (
          <p className="tipo-corpo-pequeno text-text-tertiary">
            Os dados cadastrais são atualizados automaticamente. Clique em “Consultar…” para escolher
            quais níveis buscar (multas, gravame, roubo/furto, sinistro, leilão).
          </p>
        ) : (
          <>
            {/* Status cadastral + restrição principal */}
            <div className="flex flex-wrap items-center gap-2">
              {dados!.situacao && <Chip>{dados!.situacao}</Chip>}
              {dados!.procedencia && <Chip>{dados!.procedencia}</Chip>}
              {(dados!.municipio || dados!.uf_placa) && (
                <span className="tipo-corpo-pequeno text-text-tertiary">
                  {[dados!.municipio, dados!.uf_placa].filter(Boolean).join(' / ')}
                </span>
              )}
            </div>
            {dados!.restricao && (
              <div className="flex items-center gap-2 rounded-md border border-danger/40 bg-danger/5 px-3 py-2">
                <AlertTriangle className="size-4 shrink-0 text-danger" />
                <span className="tipo-corpo-pequeno text-text">{dados!.restricao}</span>
              </div>
            )}

            {/* Detalhes ricos da consulta completa: proprietário, técnicos, leilão, roubo/furto… */}
            {dados!.detalhes.map((sec, i) => (
              <Secao key={i} titulo={sec.titulo}>
                <div
                  className={
                    'flex flex-col gap-0.5 rounded-md border px-3 py-2 ' +
                    (sec.alerta ? 'border-danger/40 bg-danger/5' : 'border-border')
                  }
                >
                  {sec.itens
                    .filter((it) => it.valor)
                    .map((it, j) => (
                      <p key={j} className="flex justify-between gap-3 tipo-corpo-pequeno">
                        <span className="text-text-tertiary">{it.rotulo}</span>
                        <span className="text-text">{it.valor}</span>
                      </p>
                    ))}
                </div>
              </Secao>
            ))}

            {dados!.gravames.length > 0 && (
              <Secao titulo="Gravame (restrição financeira)">
                {dados!.gravames.map((g, i) => (
                  <p key={i} className="tipo-corpo-pequeno text-text">
                    {[g.tipo, g.agente, g.contrato && `contrato ${g.contrato}`]
                      .filter(Boolean)
                      .join(' · ')}
                    {g.situacao && <span className="text-text-tertiary"> ({g.situacao})</span>}
                  </p>
                ))}
              </Secao>
            )}

            {dados!.multas.length > 0 && (
              <Secao titulo={`Multas (${dados!.multas.length})`}>
                <div className="divide-y divide-border overflow-hidden rounded-md border border-border">
                  {dados!.multas.map((m) => (
                    <div key={m.auto_infracao} className="flex flex-col gap-0.5 px-3 py-2">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[15px] text-text">{m.descricao || 'Infração'}</span>
                        <span className="tipo-dado text-text">{reais(m.valor)}</span>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 tipo-corpo-pequeno text-text-tertiary">
                        <span>auto {m.auto_infracao}</span>
                        {m.data_infracao && <span>· {data(m.data_infracao)}</span>}
                        {m.local && <span>· {m.local}</span>}
                        {typeof m.pontos === 'number' && <span>· {m.pontos} pts</span>}
                        {m.situacao && <Chip>{m.situacao}</Chip>}
                      </div>
                    </div>
                  ))}
                </div>
              </Secao>
            )}

            {dados!.debitos.length > 0 && (
              <Secao titulo="Débitos">
                {dados!.debitos.map((d, i) => (
                  <p
                    key={i}
                    className="flex items-center justify-between gap-2 tipo-corpo-pequeno text-text"
                  >
                    <span>
                      {[d.tipo?.toUpperCase(), d.exercicio, d.descricao].filter(Boolean).join(' · ')}
                      {d.situacao && <span className="text-text-tertiary"> ({d.situacao})</span>}
                    </span>
                    <span className="tipo-dado">{reais(d.valor)}</span>
                  </p>
                ))}
              </Secao>
            )}

            {dados!.licenciamentos.length > 0 && (
              <Secao titulo="Licenciamento">
                {dados!.licenciamentos.map((l) => (
                  <p key={l.exercicio} className="tipo-corpo-pequeno text-text">
                    {l.exercicio} <span className="text-text-tertiary">— {l.situacao ?? '—'}</span>
                  </p>
                ))}
              </Secao>
            )}

            {dados!.consulta_veicular_em && (
              <p className="tipo-corpo-pequeno text-text-tertiary">
                Última consulta: {new Date(dados!.consulta_veicular_em).toLocaleString('pt-BR')}
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function Secao({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <div>
      <span className="tipo-rotulo text-text-tertiary">{titulo}</span>
      <div className="mt-1 flex flex-col gap-1">{children}</div>
    </div>
  );
}
